/**
 * WHAT INSIGHTS IS LOOKING AT — this board or all of them, over how long.
 *
 * PER BROWSER, like the Arrange layout and the backlog filters (insights-layout,
 * filters), and for the same reason: which board a person watches and how far
 * back they look is their own view, not a setting they impose on everyone else.
 *
 * `scope` is which work the reads run over — the board that is open, or every
 * board flattened together (flow only; the portfolio owns RAG, schedule and
 * budget across boards). `window` is the number of days the rate-and-recent reads
 * cover — throughput and the flow diagram scale their own spans from it.
 */
export type InsightsScope = 'board' | 'all';

export interface InsightsView {
  scope: InsightsScope;
  window: number;
}

/** The windows offered, in days. A fortnight for "what's happening now", a month
 *  as the default working period, a quarter for a trend that outlives a sprint. */
export const WINDOW_CHOICES = [14, 28, 90] as const;
export const DEFAULT_WINDOW = 28;

export const WINDOW_LABEL: Record<number, string> = {
  14: 'Rolling 14 days',
  28: 'Rolling 28 days',
  90: 'Rolling 90 days',
};

export const DEFAULT_VIEW: InsightsView = { scope: 'board', window: DEFAULT_WINDOW };

const KEY = 'flightdeck-insights-scope';

export function readInsightsView(): InsightsView {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (!raw || typeof raw !== 'object') return DEFAULT_VIEW;
    const scope: InsightsScope = raw.scope === 'all' ? 'all' : 'board';
    const window = (WINDOW_CHOICES as readonly number[]).includes(raw.window) ? raw.window : DEFAULT_WINDOW;
    return { scope, window };
  } catch {
    return DEFAULT_VIEW;
  }
}

export function writeInsightsView(v: InsightsView): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* A browser that will not store this still works; it just forgets the choice
       between visits, which is a smaller loss than a thrown error on a dashboard. */
  }
}

/**
 * How many weekly columns a trend read (throughput, the flow diagram) draws for a
 * given window. A trend needs a floor of weeks to read as a trend at all — two
 * points is not a shape — so a short window still shows eight, and a long one
 * stretches to cover its whole span.
 */
export const weeksFor = (windowDays: number): number => Math.max(8, Math.ceil(windowDays / 7));
